import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogoMark } from "../../components/Logo";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (isRegister) await register(form.name, form.email, form.password);
      else await login(form.email, form.password);
      toast.success(isRegister ? "Welcome to Sentry" : "Signed in");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auth failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="starfield text-cream flex flex-col justify-between p-10 relative overflow-hidden">
        <Link to="/" className="inline-flex items-center gap-3 relative z-10">
          <LogoMark size={40} />
          <div className="font-display text-xl font-bold">Sentry</div>
        </Link>
        <div className="relative z-10 max-w-md">
          <div className="eyebrow text-[#FFD98A] mb-3">The Night Watch</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">Every AI request. Screened, logged, and provable.</h2>
          <p className="text-cream/70 mt-4">Sign in to the Observatory. Every decision your models make — one signed audit trail away.</p>
        </div>
        <div className="relative z-10 text-xs text-cream/50">© 2026 Lunar Occultation, Inc.</div>
      </div>

      <div className="flex items-center justify-center p-10 bg-cream">
        <form onSubmit={submit} data-testid={`${mode}-form`} className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-bold text-navy">{isRegister ? "Create your tenant" : "Sign in"}</h1>
          <p className="ink mt-1 text-sm">{isRegister ? "Free tier includes 12 prebuilt policies and 90-day evidence retention." : "Welcome back to the Observatory."}</p>
          <div className="space-y-3 mt-8">
            {isRegister && (
              <div>
                <label className="eyebrow text-navy/70 text-xs">Full name</label>
                <input data-testid="auth-name" required value={form.name} onChange={e=>setForm({...form,name:e.target.value})}
                  className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[rgba(17,30,51,0.15)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
              </div>
            )}
            <div>
              <label className="eyebrow text-navy/70 text-xs">Email</label>
              <input data-testid="auth-email" type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[rgba(17,30,51,0.15)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
            </div>
            <div>
              <label className="eyebrow text-navy/70 text-xs">Password</label>
              <input data-testid="auth-password" type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form,password:e.target.value})}
                className="w-full mt-1 px-3 py-2.5 rounded-lg border border-[rgba(17,30,51,0.15)] bg-white outline-none focus:ring-2 focus:ring-[#FF6B35]/40" />
            </div>
          </div>
          <button data-testid="auth-submit" disabled={loading} className="btn-primary w-full justify-center mt-6">
            {loading && <Loader2 size={14} className="animate-spin"/>}
            {isRegister ? "Create account" : "Sign in"}
          </button>
          <div className="mt-4 text-sm ink">
            {isRegister ? <>Already have an account? <Link to="/login" className="text-[#FF6B35] font-semibold">Sign in</Link></>
                       : <>Need an account? <Link to="/register" className="text-[#FF6B35] font-semibold">Create one</Link></>}
          </div>
        </form>
      </div>
    </div>
  );
}
